<script setup lang="ts">
import MonthPicker from '@/corePagesModule/components/MonthPicker.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import Mdi from '@/corePagesModule/components/Mdi.vue';
import { i18n } from '@/i18n';
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { IYearMonth } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { useRouter } from 'vue-router';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { IInventoryTemplatePreset } from '../domains/entities/InventoryTemplate';

// Composables
import { useTemplateSelectableItems } from '../composables/useTemplateSelectableItems';
import { useTemplateDragDropReorder } from '../composables/useTemplateDragDropReorder';
import { useTemplatePreviewHeaders } from '../composables/useTemplatePreviewHeaders';
import { useInventoryTemplatePresets } from '../composables/useInventoryTemplatePresets';

// Types and Constants
import { OutputFormat, IMasterItem, IOutputDataItem, PreviewHeaderType } from '../domains/entities/InventoryTemplate';
import {
  getInitialMasterItems,
  getInitialOutputDataItems,
  MAX_PREVIEW_HEADERS,
  isSingleFieldOutputKey,
} from '../constants/inventoryTemplateConstants';

// Template functions
import {
  exportInventoryExcel,
  downloadExcelFromUrl,
  ExportInventoryExcelParams,
  getTemplateFilterState,
  clearTemplateFilterState,
} from './useInventoryStore/inventoryTemplate';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import axios from 'axios';

const t = i18n.t;
const router = useRouter();

/**
 * プレビューヘッダーのラベルを取得（i18n対応）
 * @param header - プレビューヘッダー
 * @returns 翻訳されたラベル
 */
const getHeaderLabel = (header: {
  type: string;
  masterKey?: string;
  outputDataKey?: string;
  yearMonth?: { year: number; month: number };
}): string => {
  if (header.type === 'master' && header.masterKey) {
    return t(header.masterKey);
  }
  if (header.type === 'outputData' && header.outputDataKey) {
    if (header.yearMonth) {
      return `${t('displayYearMonth', { year: header.yearMonth.year, month: header.yearMonth.month })}_${t(header.outputDataKey)}`;
    }
    return t(header.outputDataKey);
  }
  return '';
};

// Master Items - using composable
const {
  items: masterItems,
  selectedItems: selectedMasterItems,
  handleItemChange: handleMasterItemChange,
  reorderItems: reorderMasterItems,
  resetSelection: resetMasterItemSelection,
} = useTemplateSelectableItems<IMasterItem>(getInitialMasterItems());

// Output Data Items - using composable
const {
  items: outputDataItems,
  selectedItems: selectedOutputDataItems,
  handleItemChange: handleOutputDataItemChange,
  reorderItems: reorderOutputDataItems,
  moveSingleFieldItem: moveOutputDataSingleFieldItem,
  resetSelection: resetOutputDataItemSelection,
} = useTemplateSelectableItems<IOutputDataItem>(getInitialOutputDataItems());

// Output Settings
const currentYearMonth = DateService.convertDateToIYearMonth(new Date());
const startYearMonth = ref<IYearMonth>(currentYearMonth);
const endYearMonth = ref<IYearMonth>(DateService.getShiftedIYearMonthByMonths(currentYearMonth.year, currentYearMonth.month, 3));
const outputFormat = ref<OutputFormat>(OutputFormat.BY_MONTH);

const locale = computed(() => i18n.locale);

// Output Condition Presets（組織内で共有される保存済みの出力条件）
const { presets, fetchPresets, savePreset, updatePreset, deletePreset } = useInventoryTemplatePresets();

const showSaveModal = ref(false);
const saveModalName = ref('');
const activePresetId = ref<string | null>(null);
const editingPresetId = ref<string | null>(null);
const editingPresetName = computed(() => presets.value.find((preset) => preset.presetId === editingPresetId.value)?.name ?? '');

/**
 * 2つの年月の差（月単位）を計算する
 * @param target - 対象の年月
 * @param base - 基準となる年月
 * @returns targetがbaseから何ヶ月後か（負の値は前）
 */
const calculateMonthOffset = (target: IYearMonth, base: IYearMonth): number => {
  return (target.year - base.year) * 12 + (target.month - base.month);
};

/**
 * 現在画面に入っている出力条件から、保存・更新用のリクエストを組み立てる
 * 出力期間は「この操作を行った瞬間の今月」を基準にした相対オフセットとして計算する
 * @param name - 書式名
 */
const buildPresetUpsertRequest = (name: string) => {
  const now = DateService.convertDateToIYearMonth(new Date());
  return {
    name,
    masterItems: selectedMasterItems.value.map((item) => item.key),
    outputDataItems: selectedOutputDataItems.value.map((item) => item.key),
    singleFieldPositions: singleFieldPositions.value ?? null,
    startMonthOffset: calculateMonthOffset(startYearMonth.value, now),
    endMonthOffset: calculateMonthOffset(endYearMonth.value, now),
    outputFormat: outputFormat.value,
  };
};

/**
 * 出力書式の保存モーダルを開く
 */
const openSaveModal = () => {
  saveModalName.value = '';
  showSaveModal.value = true;
};

/**
 * 出力書式の保存モーダルを閉じる
 */
const closeSaveModal = () => {
  showSaveModal.value = false;
};

/**
 * 出力書式を新規保存する
 */
const handleSaveModalConfirm = async () => {
  if (!saveModalName.value.trim()) return;

  const result = await savePreset(buildPresetUpsertRequest(saveModalName.value.trim()));
  if (result.status.type === 'success') {
    activePresetId.value = result.status.data.presetId;
    showSaveModal.value = false;
  } else {
    toast.error(t('出力書式の保存に失敗しました'));
  }
};

/**
 * 指定したキーの配列だけを選択状態にする（配列の順序で選択順が振られる）
 * @param keys - 選択状態にするキーの配列
 * @param items - 対象の項目一覧
 * @param resetSelection - 選択状態をリセットする関数
 * @param handleItemChange - 選択状態を変更する関数
 */
const applySelectedKeys = <T extends { key: string }>(
  keys: string[],
  items: T[],
  resetSelection: () => void,
  handleItemChange: (item: T, checked: boolean) => void
) => {
  resetSelection();
  keys.forEach((key) => {
    const item = items.find((i) => i.key === key);
    if (item) {
      handleItemChange(item, true);
    }
  });
};

/**
 * 出力条件プリセットを画面に読み込む
 * @param preset - 読み込むプリセット
 */
const loadPreset = (preset: IInventoryTemplatePreset) => {
  applySelectedKeys(preset.masterItemKeys, masterItems.value, resetMasterItemSelection, handleMasterItemChange);
  applySelectedKeys(preset.outputDataItemKeys, outputDataItems.value, resetOutputDataItemSelection, handleOutputDataItemChange);

  // 開始月・終了月は「読み込んだ瞬間」の今月を基準に、保存されているオフセットから再計算する
  const now = DateService.convertDateToIYearMonth(new Date());
  startYearMonth.value = DateService.getShiftedIYearMonthByMonths(now.year, now.month, preset.startMonthOffset);
  endYearMonth.value = DateService.getShiftedIYearMonthByMonths(now.year, now.month, preset.endMonthOffset);

  outputFormat.value = preset.outputFormat;

  // 現在庫など単一フィールド項目の挿入位置を復元する
  // （ここまでの選択・期間・出力形式の変更により、previewHeadersは最新の状態になっている）
  const monthlyHeaders = previewHeaders.value.filter(
    (header) => header.type === PreviewHeaderType.OUTPUT_DATA && !isSingleFieldOutputKey(header.outputDataKey)
  );
  Object.entries(preset.singleFieldPositions ?? {}).forEach(([key, index]) => {
    const targetHeader = monthlyHeaders[index];
    if (targetHeader) {
      moveOutputDataSingleFieldItem(key, targetHeader.id);
    }
  });

  activePresetId.value = preset.presetId;
};

/**
 * プリセットのチップをクリックした際のハンドラー（読み込み）
 * @param preset - 対象のプリセット
 */
const handlePresetChipClick = (preset: IInventoryTemplatePreset) => {
  if (editingPresetId.value) return;
  loadPreset(preset);
};

/**
 * 鉛筆アイコンを押した際のハンドラー（編集モードに入る）
 * @param preset - 対象のプリセット
 */
const handlePresetEditClick = (preset: IInventoryTemplatePreset) => {
  loadPreset(preset);
  editingPresetId.value = preset.presetId;
};

/**
 * 編集モードをキャンセルする
 * 編集中に変更した内容を破棄し、プリセットの元の値に戻す
 */
const handleCancelEdit = () => {
  const preset = presets.value.find((p) => p.presetId === editingPresetId.value);
  if (preset) {
    loadPreset(preset);
  }
  editingPresetId.value = null;
};

/**
 * 編集内容を保存する
 */
const handleSaveEdit = async () => {
  if (!editingPresetId.value) return;

  const result = await updatePreset(editingPresetId.value, buildPresetUpsertRequest(editingPresetName.value));
  if (result.status.type === 'success') {
    activePresetId.value = result.status.data.presetId;
    editingPresetId.value = null;
  } else {
    toast.error(t('出力書式の更新に失敗しました'));
  }
};

/**
 * ゴミ箱アイコンを押した際のハンドラー（削除）
 * @param preset - 対象のプリセット
 */
const handlePresetDeleteClick = async (preset: IInventoryTemplatePreset) => {
  const confirmed = await showInfoModal({
    modalTitle: t('出力書式を削除'),
    modalDescription: t('出力書式「{name}」を削除します。よろしいですか？', { name: preset.name }),
    backButtonLabel: t('キャンセル'),
    nextButtonLabel: t('削除する'),
    icon: 'error',
    colorStyle: 'error',
  });
  if (!confirmed) return;

  const result = await deletePreset(preset.presetId);
  if (result.status.type === 'success') {
    if (activePresetId.value === preset.presetId) activePresetId.value = null;
    if (editingPresetId.value === preset.presetId) editingPresetId.value = null;
  } else {
    toast.error(t('出力書式の削除に失敗しました'));
  }
};

// Preview Headers - using composable
const { previewHeaders, showPreview, otherColumnsCount } = useTemplatePreviewHeaders(
  selectedMasterItems,
  selectedOutputDataItems,
  startYearMonth,
  endYearMonth,
  outputFormat
);

// Drag and Drop - using composable
const { draggedHeaderId, dragOverIndex, handleDragStart, handleDragEnd, handleDragOver, handleDrop } =
  useTemplateDragDropReorder(outputFormat);

/**
 * ドロップハンドラーのラッパー
 * @param event - ドラッグイベント
 * @param targetIndex - ターゲットインデックス
 */
const onDrop = (event: DragEvent, targetIndex: number) => {
  handleDrop(event, targetIndex, previewHeaders.value, reorderMasterItems, reorderOutputDataItems, moveOutputDataSingleFieldItem);
};

// Tooltip State for NX Explanation
const showTooltip = ref(false);
const tooltipRef = ref<HTMLElement | null>(null);

/**
 * ツールチップの表示切り替え
 */
const toggleTooltip = () => {
  showTooltip.value = !showTooltip.value;
};

/**
 * ツールチップを非表示にする（外部クリック検出用）
 * @param event - マウスイベント
 */
const handleClickOutside = (event: MouseEvent) => {
  if (tooltipRef.value && event.target && !tooltipRef.value.contains(event.target as Node)) {
    showTooltip.value = false;
  }
};

/**
 * ダウンロードボタンの有効性判定
 */
const isDownloadEnabled = computed(() => {
  const hasMasterItem = selectedMasterItems.value.length > 0;
  const hasOutputData = selectedOutputDataItems.value.length > 0;
  const hasPeriod = startYearMonth.value && endYearMonth.value;
  const hasFormat = outputFormat.value !== null;

  return hasMasterItem && hasOutputData && hasPeriod && hasFormat;
});

/**
 * 単一フィールド項目の挿入位置を計算
 * プレビューヘッダーから単一フィールド項目の位置を取得し、
 * 月別ヘッダー配列内でのインデックスとして返す
 */
const singleFieldPositions = computed<{ [key: string]: number } | undefined>(() => {
  // 出力データ項目のみを抽出（マスタ項目を除く）
  const outputHeaders = previewHeaders.value.filter((h) => h.type === PreviewHeaderType.OUTPUT_DATA);

  // 単一フィールド項目の位置を計算
  const positions: { [key: string]: number } = {};

  outputHeaders.forEach((header, idx) => {
    if (isSingleFieldOutputKey(header.outputDataKey)) {
      // この単一フィールド項目より前にある月別ヘッダーの数がインデックス
      const monthlyHeadersBefore = outputHeaders.slice(0, idx).filter((h) => !isSingleFieldOutputKey(h.outputDataKey)).length;
      positions[header.outputDataKey!] = monthlyHeadersBefore;
    }
  });

  return Object.keys(positions).length > 0 ? positions : undefined;
});

/**
 * 戻るボタンのハンドラー
 */
const handleBack = () => {
  router.back();
};

// Loading state
const isDownloading = ref(false);

/**
 * エラーの種類を判定してユーザーフレンドリーなメッセージを表示
 * @param error - エラーオブジェクト
 */
const handleExportError = (error: unknown) => {
  // Axiosタイムアウトエラーの判定
  if (axios.isAxiosError(error)) {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      toast.error(t('データ量が多いため処理がタイムアウトしました。出力期間を短くするか、項目数を減らしてください。'));
      return;
    }
    // ネットワークエラー（接続なし）
    if (error.code === 'ERR_NETWORK' || !error.response) {
      toast.error(t('ネットワークエラーが発生しました。接続を確認してください。'));
      return;
    }
    // サーバーエラー (5xx)
    if (error.response && error.response.status >= 500) {
      toast.error(t('サーバーエラーが発生しました。しばらく待ってから再度お試しください。'));
      return;
    }
  }
  // その他のエラー
  toast.error(t('Excelエクスポートに失敗しました'));
};

/**
 * Excelダウンロードボタンのハンドラー
 */
const handleDownload = async () => {
  if (!isDownloadEnabled.value || isDownloading.value) return;

  isDownloading.value = true;

  try {
    // Build params for inventoryTemplate
    const params: ExportInventoryExcelParams = {
      masterItems: selectedMasterItems.value.map((item) => item.key),
      outputDataItems: selectedOutputDataItems.value.map((item) => item.key),
      startYearMonth: startYearMonth.value,
      endYearMonth: endYearMonth.value,
      outputFormat: outputFormat.value,
      singleFieldPositions: singleFieldPositions.value,
      language: locale.value === 'en' ? 'en' : 'ja',
      filterItems: getTemplateFilterState().filterItems,
    };

    const result = await exportInventoryExcel(params);

    if (result.status.type === 'success') {
      const { download_url, file_name } = result.status.data;
      downloadExcelFromUrl(download_url, file_name);
    } else {
      // API returned error response
      const error = result.status.error;
      if (error.statusCode === 504 || error.statusCode === 408) {
        toast.error(t('データ量が多いため処理がタイムアウトしました。出力期間を短くするか、項目数を減らしてください。'));
      } else if (error.statusCode >= 500) {
        toast.error(t('サーバーエラーが発生しました。しばらく待ってから再度お試しください。'));
      } else {
        toast.error(t('Excelエクスポートに失敗しました'));
      }
      console.error('Export failed:', error);
    }
  } catch (error) {
    console.error('Export error:', error);
    handleExportError(error);
  } finally {
    isDownloading.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  fetchPresets();
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  // フィルター情報をクリア
  clearTemplateFilterState();
});
</script>

<template>
  <section class="inventory-template-page">
    <header class="inventory-template-page__header">
      <h1 class="inventory-template-page__title">{{ t('データ出力') }}</h1>
    </header>

    <!-- Output Preview Section -->
    <section
      v-if="showPreview"
      class="inventory-template-page__preview"
      :class="{ 'inventory-template-page__preview--editing': editingPresetId }"
    >
      <div v-if="editingPresetId" class="inventory-template-page__edit-banner">
        <span class="inventory-template-page__edit-banner-label">
          <SvgLoader name="editPen" width="16" height="16" />
          {{ t('編集中') }}: {{ editingPresetName }}
        </span>
        <span class="inventory-template-page__edit-banner-hint">{{ t('下の設定を変更して「変更を保存」を押してください') }}</span>
        <div class="inventory-template-page__edit-banner-actions">
          <TextButton :button-style="'btn-secondary'" :size="'btn-md'" :on-click="handleCancelEdit">
            {{ t('キャンセル') }}
          </TextButton>
          <TextButton :button-style="'btn-primary'" :size="'btn-md'" :on-click="handleSaveEdit">
            {{ t('変更を保存') }}
          </TextButton>
        </div>
      </div>

      <div class="inventory-template-page__preview-header-row">
        <h2 class="inventory-template-page__preview-title">{{ t('出力プレビュー') }}</h2>
        <TextButton
          :button-style="'btn-secondary'"
          :size="'btn-md'"
          :disabled="!isDownloadEnabled || !!editingPresetId"
          :on-click="openSaveModal"
        >
          {{ t('出力書式を保存') }}
        </TextButton>
      </div>
      <p class="inventory-template-page__preview-description">
        {{ t('出力ヘッダーの並びを表示しています。') }}
      </p>
      <div class="inventory-template-page__preview-headers">
        <span
          v-for="(header, index) in previewHeaders.slice(0, MAX_PREVIEW_HEADERS)"
          :key="header.id"
          class="inventory-template-page__preview-tag"
          :class="{
            'inventory-template-page__preview-tag--dragging': draggedHeaderId === header.id,
            'inventory-template-page__preview-tag--drag-over': dragOverIndex === index,
          }"
          :draggable="true"
          @dragstart="handleDragStart($event, header)"
          @dragend="handleDragEnd($event)"
          @dragover.prevent="handleDragOver($event, index)"
          @drop="onDrop($event, index)"
        >
          {{ getHeaderLabel(header) }}
        </span>
        <span v-if="otherColumnsCount > 0" class="inventory-template-page__preview-tag inventory-template-page__preview-tag--more">
          ...{{ t('他{count}列', { count: otherColumnsCount }) }}
        </span>
      </div>

      <div v-if="presets.length > 0" class="inventory-template-page__preset-list">
        <p class="inventory-template-page__preset-list-hint">
          <Mdi icon="save" class="inventory-template-page__preset-list-hint-icon" />
          <strong class="inventory-template-page__preset-list-hint-title">{{ t('保存済み出力書式') }}</strong>
          — {{ t('クリックで読み込み、鉛筆アイコンで編集') }}
        </p>
        <div class="inventory-template-page__preset-chips">
          <div
            v-for="preset in presets"
            :key="preset.presetId"
            class="inventory-template-page__preset-chip"
            :class="{
              'inventory-template-page__preset-chip--active': activePresetId === preset.presetId && editingPresetId !== preset.presetId,
              'inventory-template-page__preset-chip--editing': editingPresetId === preset.presetId,
            }"
          >
            <div class="inventory-template-page__preset-chip-accent" />
            <div class="inventory-template-page__preset-chip-content">
              <SvgLoader
                v-if="activePresetId === preset.presetId && editingPresetId !== preset.presetId"
                name="checkWhite"
                width="14"
                height="14"
                class="inventory-template-page__preset-chip-check"
              />
              <button class="inventory-template-page__preset-chip-label" type="button" @click="handlePresetChipClick(preset)">
                <template v-if="editingPresetId === preset.presetId">{{ t('編集中') }}: </template>
                {{ preset.name }}
              </button>
              <div class="inventory-template-page__preset-chip-divider" />
              <div class="inventory-template-page__preset-chip-actions">
                <button
                  class="inventory-template-page__preset-chip-action inventory-template-page__preset-chip-action--edit"
                  type="button"
                  :aria-label="t('編集')"
                  @click="handlePresetEditClick(preset)"
                >
                  <Mdi icon="edit" class="inventory-template-page__preset-chip-action-icon" />
                </button>
                <button
                  class="inventory-template-page__preset-chip-action inventory-template-page__preset-chip-action--delete"
                  type="button"
                  :aria-label="t('削除')"
                  @click="handlePresetDeleteClick(preset)"
                >
                  <SvgLoader name="trash-box" width="13" height="13" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="inventory-template-page__preview-actions">
        <button
          class="inventory-template-page__download-button"
          :class="{
            'inventory-template-page__download-button--disabled': !isDownloadEnabled || isDownloading,
            'inventory-template-page__download-button--loading': isDownloading,
          }"
          :disabled="!isDownloadEnabled || isDownloading"
          @click="handleDownload"
        >
          {{ isDownloading ? t('ダウンロード中...') : t('Excelをダウンロード') }}
          <SvgLoader v-if="!isDownloading" name="downloadSmall" width="16" height="16" />
        </button>
        <div class="inventory-template-page__back-button-wrapper">
          <TextButton :button-style="'btn-secondary'" :size="'btn-md'" :on-click="handleBack">
            {{ t('戻る') }}
          </TextButton>
        </div>
      </div>
    </section>

    <!-- Three Column Sections -->
    <div class="inventory-template-page__sections">
      <!-- Master Items Section -->
      <section class="inventory-template-page__section">
        <h3 class="inventory-template-page__section-title">{{ t('マスタ項目') }}</h3>
        <p class="inventory-template-page__section-description">
          {{ t('出力する項目を選択してください') }}
        </p>
        <div class="inventory-template-page__checkbox-grid">
          <label v-for="item in masterItems" :key="item.key" class="inventory-template-page__checkbox-item">
            <input
              type="checkbox"
              :checked="item.checked"
              @change="handleMasterItemChange(item, ($event.target as HTMLInputElement).checked)"
            />
            <span>{{ t(item.key) }}</span>
          </label>
        </div>
      </section>

      <!-- Output Data Section -->
      <section class="inventory-template-page__section">
        <h3 class="inventory-template-page__section-title">
          <span>{{ t('出力データ') }}</span>
          <div ref="tooltipRef" class="inventory-template-page__tooltip-wrapper">
            <SvgLoader name="questionIcon" class="inventory-template-page__help-icon" @click="toggleTooltip" />
            <div v-if="showTooltip" class="inventory-template-page__tooltip">
              <p>{{ t('客先内示(N-1)とは、受領月が前月の内示を表示します。') }}</p>
              <p>{{ t('例: 2025年10月の内示を表示する場合、受領月は2025年9月の内示が表示されます。') }}</p>
              <p>{{ t('仕入先内示(N-1)とは、作成月が前月の内示を表示します。') }}</p>
              <p>
                {{ t('最新客先内示・最新仕入先内示は、対象月に登録されている内示のうち、受領月・作成月が最も新しいものを表示します。') }}
              </p>
            </div>
          </div>
        </h3>
        <p class="inventory-template-page__section-description">
          {{ t('出力する数値データを選択してください') }}
        </p>
        <div class="inventory-template-page__checkbox-grid">
          <label v-for="item in outputDataItems" :key="item.key" class="inventory-template-page__checkbox-item">
            <input
              type="checkbox"
              :checked="item.checked"
              @change="handleOutputDataItemChange(item, ($event.target as HTMLInputElement).checked)"
            />
            <span>{{ t(item.key) }}</span>
          </label>
        </div>
      </section>

      <!-- Output Settings Section -->
      <section class="inventory-template-page__section">
        <h3 class="inventory-template-page__section-title">
          <span>{{ t('出力設定') }}</span>
          <Mdi icon="keyboard_arrow_right" class="inventory-template-page__arrow-icon" aria-hidden="true" />
        </h3>
        <p class="inventory-template-page__section-description">
          {{ t('出力期間と出力形式を選択してください') }}
        </p>
        <div class="inventory-template-page__settings-content">
          <div class="inventory-template-page__period">
            <label class="inventory-template-page__period-label">{{ t('出力期間') }}</label>
            <div class="inventory-template-page__period-inputs">
              <div class="inventory-template-page__period-item">
                <label class="inventory-template-page__period-item-label">{{ t('開始月') }}</label>
                <div class="inventory-template-page__calendar-wrapper">
                  <MonthPicker
                    class="inventory-template-page__calendar inventory-template-page-period-calendar"
                    :model-value="startYearMonth"
                    :locale="locale"
                    prop-format="yyyy / MM"
                    :max-date="endYearMonth"
                    @update:model-value="startYearMonth = $event"
                  />
                  <Mdi icon="calendar_today" class="inventory-template-page__calendar-icon" />
                </div>
              </div>
              <div class="inventory-template-page__period-item">
                <label class="inventory-template-page__period-item-label">{{ t('終了月') }}</label>
                <div class="inventory-template-page__calendar-wrapper">
                  <MonthPicker
                    class="inventory-template-page__calendar inventory-template-page-period-calendar"
                    :model-value="endYearMonth"
                    :locale="locale"
                    prop-format="yyyy / MM"
                    :min-date="startYearMonth"
                    @update:model-value="endYearMonth = $event"
                  />
                  <Mdi icon="calendar_today" class="inventory-template-page__calendar-icon" />
                </div>
              </div>
            </div>
          </div>
          <div class="inventory-template-page__format">
            <label class="inventory-template-page__format-label">{{ t('出力形式') }}</label>
            <div class="inventory-template-page__format-options">
              <label
                class="inventory-template-page__radio-item"
                :class="{ 'inventory-template-page__radio-item--selected': outputFormat === 'byMonth' }"
              >
                <input
                  type="radio"
                  name="outputFormat"
                  value="byMonth"
                  :checked="outputFormat === 'byMonth'"
                  @change="outputFormat = 'byMonth'"
                />
                <div class="inventory-template-page__radio-content">
                  <span class="inventory-template-page__radio-label">{{ t('月単位で横並びにまとめる') }}</span>
                  <span class="inventory-template-page__radio-example">
                    {{ t('例:1月(在庫、販売実績...)→2月(在庫、販売実績...)') }}
                  </span>
                </div>
              </label>
              <label
                class="inventory-template-page__radio-item"
                :class="{ 'inventory-template-page__radio-item--selected': outputFormat === 'byDataType' }"
              >
                <input
                  type="radio"
                  name="outputFormat"
                  value="byDataType"
                  :checked="outputFormat === 'byDataType'"
                  @change="outputFormat = 'byDataType'"
                />
                <div class="inventory-template-page__radio-content">
                  <span class="inventory-template-page__radio-label">{{ t('データ種類単位で横並びにまとめる') }}</span>
                  <span class="inventory-template-page__radio-example">
                    {{ t('例:在庫(1月、2月...)→販売実績(1月、2月...)') }}
                  </span>
                </div>
              </label>
            </div>
          </div>
        </div>
      </section>
    </div>

    <InfoModal
      :modal-state="showSaveModal"
      :back-button-label="t('キャンセル')"
      :next-button-label="t('保存')"
      :is-disabled-next-button="!saveModalName.trim()"
      @on-back="closeSaveModal"
      @on-next="handleSaveModalConfirm"
    >
      <template #title>{{ t('出力書式を保存') }}</template>
      <div class="inventory-template-page__save-modal">
        <label class="inventory-template-page__save-modal-label">{{ t('書式名') }}</label>
        <input
          v-model="saveModalName"
          type="text"
          class="inventory-template-page__save-modal-input"
          :placeholder="t('例: 標準月次レポート')"
        />
      </div>
    </InfoModal>
  </section>
</template>

<style scoped lang="scss">
.inventory-template-page {
  display: grid;
  gap: 24px;
  padding: 24px;

  &__header {
    display: grid;
    gap: 0;
  }

  &__title {
    margin: 0;
    font-size: 28px;
    font-weight: 700;
    color: $textDefault;
  }

  &__preview {
    background-color: $semanticWhite;
    border: 1px solid $borderDefault;
    border-radius: 8px;
    padding: 24px;
    display: grid;
    gap: 16px;

    &--editing {
      border-color: $semanticWarning;
      background-color: $semanticWarningSubdued;
    }
  }

  &__preview-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  &__preview-title {
    font-size: 18px;
    font-weight: 700;
    color: $textDefault;
    margin: 0;
  }

  &__edit-banner {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 16px;
    padding: 12px 16px;
    border-radius: 6px;
    background-color: $semanticWarningSubdued;
    border: 1px solid $semanticWarning;
  }

  &__edit-banner-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 700;
    color: $semanticWarning;
  }

  &__edit-banner-hint {
    font-size: 13px;
    color: $textSecondary;
    flex: 1;
  }

  &__edit-banner-actions {
    display: flex;
    gap: 8px;
  }

  &__preset-list {
    display: grid;
    gap: 8px;
  }

  &__preset-list-hint {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: $textSecondary;
    margin: 0;
  }

  &__preset-list-hint-icon {
    flex-shrink: 0;
    color: $textSecondary;
    font-size: 14px;
  }

  &__preset-list-hint-title {
    font-weight: 700;
  }

  &__preset-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__preset-chip {
    display: flex;
    align-items: center;
    gap: 0;
    border: 1px solid $borderDefault;
    border-radius: 6px;
    background-color: $semanticWhite;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.2s ease;

    &--active {
      border-color: $semanticPrimary;
      background-color: $semanticPrimarySubdued;
      box-shadow: 0 0 0 2px rgba($semanticPrimary, 0.15);

      .inventory-template-page__preset-chip-accent {
        background-color: $semanticPrimary;
      }
    }

    &--editing {
      border-color: $semanticWarning;
      background-color: $semanticWarningSubdued;

      .inventory-template-page__preset-chip-accent {
        background-color: $semanticWarning;
      }
    }
  }

  &__preset-chip-accent {
    width: 4px;
    align-self: stretch;
    flex-shrink: 0;
    background-color: transparent;
  }

  &__preset-chip-content {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 4px 8px 10px;
  }

  &__preset-chip-check {
    flex-shrink: 0;

    :deep(svg) {
      width: 14px;
      height: 14px;
    }

    // checkWhite.svg hardcodes fill="white" on its path, so override that directly
    // rather than relying on `color`/currentColor (which the file doesn't use).
    :deep(path) {
      fill: $semanticPrimary;
    }
  }

  &__preset-chip-label {
    background: none;
    border: none;
    padding: 0;
    font-size: 14px;
    line-height: 1;
    white-space: nowrap;
    font-weight: 400;
    color: $textDefault;
    cursor: pointer;

    .inventory-template-page__preset-chip--active & {
      font-weight: 700;
      color: $semanticPrimary;
    }

    .inventory-template-page__preset-chip--editing & {
      font-weight: 700;
      color: $semanticWarning;
    }
  }

  &__preset-chip-divider {
    width: 1px;
    height: 16px;
    background-color: $borderDefault;
    flex-shrink: 0;
  }

  &__preset-chip-actions {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  &__preset-chip-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    padding: 4px;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;
    color: $textSecondary;

    :deep(svg) {
      width: 13px;
      height: 13px;
    }

    &--edit {
      .inventory-template-page__preset-chip--active & {
        color: $semanticPrimary;
      }

      &:hover {
        background-color: $semanticPrimarySubdued02;
      }
    }

    &--delete {
      &:hover {
        background-color: $semanticErrorSubdued;
        color: $semanticError;
      }
    }
  }

  &__preset-chip-action-icon {
    font-size: 13px;
  }

  &__save-modal {
    display: grid;
    gap: 8px;
    padding: 16px;
    min-width: 320px;
  }

  &__save-modal-label {
    font-size: 14px;
    font-weight: 600;
    color: $textDefault;
  }

  &__save-modal-input {
    padding: 8px 12px;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    font-size: 14px;
  }

  &__preview-description {
    font-size: 14px;
    color: $textDefault;
    margin: 0;
  }

  &__preview-headers {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 8px 0;
    flex-wrap: wrap;
  }

  &__preview-tag {
    background-color: #f5f5f5;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    padding: 6px 12px;
    font-size: 12px;
    white-space: nowrap;
    color: $textDefault;
    cursor: move;
    transition: all 0.2s;

    &:hover {
      background-color: #eeeeee;
    }

    &--dragging {
      opacity: 0.5;
    }

    &--drag-over {
      border-color: $semanticPrimary;
      background-color: $semanticPrimarySubdued;
    }

    &--more {
      color: #666;
      font-style: italic;
      cursor: default;

      &:hover {
        background-color: #f5f5f5;
      }
    }
  }

  &__preview-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-start;
  }

  &__back-button-wrapper {
    :deep(.button_content) {
      box-shadow: none !important;
    }
  }

  &__download-button {
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: #0066cc;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover:not(&--disabled) {
      background-color: #0052a3;
    }

    &--disabled {
      background-color: #cccccc;
      color: #666666;
      cursor: not-allowed;
      opacity: 0.6;
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }

  &__sections {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    align-items: start;
  }

  &__section {
    background-color: $semanticWhite;
    border: 1px solid $borderDefault;
    border-radius: 8px;
    padding: 24px;
    display: grid;
    gap: 16px;
    align-content: start;
    height: fit-content;
  }

  &__section-title {
    font-size: 18px;
    font-weight: 700;
    color: $textDefault;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
  }

  &__help-icon {
    width: 18px;
    height: 18px;
    color: #616161;
    cursor: pointer;
    flex-shrink: 0;
    transition: color 0.2s;

    &:hover {
      color: #333333;
    }
  }

  &__tooltip-wrapper {
    position: relative;
    display: inline-block;
  }

  &__tooltip {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    background-color: #333333;
    color: white;
    padding: 12px 16px;
    border-radius: 4px;
    font-size: 12px;
    white-space: normal;
    width: 280px;
    z-index: 1000;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

    p {
      margin: 0 0 8px 0;

      &:last-child {
        margin-bottom: 0;
      }
    }

    &::before {
      content: '';
      position: absolute;
      bottom: 100%;
      right: 12px;
      border: 6px solid transparent;
      border-bottom-color: #333333;
    }
  }

  // /settings（SettingsHomeCard.vue）の見出し横矢印と同じMdi「keyboard_arrow_right」を使用。
  &__arrow-icon {
    width: 24px;
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    color: #666;
    flex-shrink: 0;
  }

  &__section-description {
    font-size: 14px;
    color: $textSecondary;
    margin: 0;
  }

  &__checkbox-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;

    &--two-columns {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  &__checkbox-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: $textDefault;
    cursor: pointer;

    input[type='checkbox'] {
      width: 16px;
      height: 16px;
      cursor: pointer;
    }
  }

  &__settings-content {
    display: grid;
    gap: 24px;
  }

  &__period {
    display: grid;
    gap: 12px;
  }

  &__period-label {
    font-size: 16px;
    font-weight: 400;
    color: $textSecondary;
    margin-bottom: 4px;
  }

  &__period-inputs {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
  }

  &__period-item {
    display: grid;
    // `auto` tracks stretch to fill any leftover container width (CSS Grid's
    // "Maximize Tracks" step); `max-content` tracks don't, keeping the label
    // and calendar tight together instead of stretched across the row.
    grid-template-columns: max-content max-content;
    gap: 8px;
    align-items: center;
  }

  &__period-item-label {
    font-size: 14px;
    color: $textDefault;
  }

  &__calendar-wrapper {
    position: relative;
  }

  &__calendar {
    width: 110px;
    height: 32px;
    background-color: $semanticWhite;
    border-radius: 4px;
    border: 1px solid $borderDefault;
    display: grid;
    place-items: center;
  }

  // /inventory-control（管理表）の希望納期カレンダーと同じMaterial Icons「calendar_today」を使用
  &__calendar-icon {
    position: absolute;
    top: 50%;
    right: 8px;
    transform: translateY(-50%);
    font-size: 16px;
    color: $textSecondary;
    pointer-events: none;
  }

  &__format {
    display: grid;
    gap: 12px;
  }

  &__format-label {
    font-size: 14px;
    font-weight: 600;
    color: $textDefault;
    margin-bottom: 4px;
  }

  &__format-options {
    display: grid;
    gap: 12px;
  }

  &__radio-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: pointer;
    padding: 12px;
    border-radius: 4px;
    border: 1px solid $borderDefault;
    background-color: $semanticWhite;
    transition: all 0.2s ease;

    &--selected {
      border: 1px solid $semanticPrimary;
      background-color: $semanticPrimarySubdued;
    }
  }

  &__radio-content {
    display: grid;
    gap: 4px;
    flex: 1;
  }

  &__radio-label {
    font-size: 14px;
    color: $textDefault;
    font-weight: 500;
  }

  &__radio-example {
    font-size: 12px;
    color: #666;
  }

  input[type='radio'] {
    width: 16px;
    height: 16px;
    margin-top: 2px;
    cursor: pointer;
    flex-shrink: 0;
  }
}
</style>

<style lang="scss">
.inventory-template-page-period-calendar input {
  font-family: Noto Sans JP;
  font-size: 14px;
  font-weight: 400;
  color: $textSecondary;
  padding: 6px 28px 6px 8px;
}

// vue-datepicker-overrides.scss（InventoryControlModule/pages/index.vue経由でグローバルに読み込まれる）が
// `.dp__input_icon { display: none; }` を設定しているため、ライブラリ自体のアイコンは元々非表示のまま。
// カレンダーアイコンは代わりに独自の`__calendar-icon`（Mdiのcalendar_today）で表示している。
</style>
